import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { roleLabel, useOrg } from "../org";

const ISSUE = {
  missing_email: "Add an email address to this profile before inviting them. You can still prepare their setup now.",
  shared_email: "This email is on more than one person in your church. Review those profiles before inviting, so KNOWN links the right one.",
  email_is_member: "Someone on your team already uses this email. Check the Team page before inviting.",
};

const RolePick = ({ role, setRole }) => {
  const { membership } = useOrg();
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {["leader", "director", "pastor", "admin"].map((r) => (
        <button key={r} aria-pressed={role === r} onClick={() => setRole(r)} className="choice h-11 px-4" data-testid={`access-role-${r}`}>{roleLabel(membership, r)}</button>
      ))}
    </div>
  );
};

const GroupPick = ({ groups, ids, setIds }) => {
  if (!groups.length) return null;
  const toggle = (id) => setIds(ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]);
  return (
    <div className="mt-4" data-testid="access-groups">
      <p className="t-meta">Care Groups they'll lead</p>
      <div className="flex flex-wrap gap-2 mt-2">
        {groups.map((g) => <button key={g.id} aria-pressed={ids.includes(g.id)} onClick={() => toggle(g.id)} className="choice h-11 px-4" data-testid={`access-group-${g.id}`}>{g.name}</button>)}
      </div>
    </div>
  );
};

const useGroups = () => {
  const { api } = useOrg();
  const [groups, setGroups] = useState([]);
  useEffect(() => { api("/groups").then(setGroups).catch(() => {}); }, [api]);
  return groups;
};

const Plan = ({ a, reload, onDone }) => {
  const { api } = useOrg();
  const groups = useGroups();
  const prep = a.prepared;
  const [role, setRole] = useState(prep?.role || "leader");
  const [ids, setIds] = useState(prep?.group_ids || []);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const canSend = a.status === "none" && !a.issue;
  const run = (next, msg) => {
    setBusy(true);
    const group_ids = role === "admin" ? [] : ids;
    api(`/people/${a.person.id}/prepare`, { method: "PUT", body: { role, group_ids } })
      .then(() => next && api(`/people/${a.person.id}/${next}`, { method: "POST", body: next === "grant" ? { role, confirm: true } : { role } }))
      .then((r) => { toast.success(r?.email_sent === false ? "Invitation created, but the email didn't send. Resend from here or Team." : msg); onDone?.(); reload(); })
      .catch((e) => toast.error(e.message)).finally(() => { setBusy(false); setConfirm(false); });
  };
  return (
    <div data-testid="access-plan">
      <RolePick role={role} setRole={setRole} />
      {role !== "admin" && <GroupPick groups={groups} ids={ids} setIds={setIds} />}
      <div className="flex flex-wrap gap-2 mt-4">
        {canSend && <button disabled={busy} onClick={() => run("invite", "Invitation sent")} className="btn btn-primary btn-sm" data-testid="access-invite-btn">Send invitation</button>}
        <button disabled={busy} onClick={() => run(null, a.status === "pending" || prep ? "Setup updated" : "Saved as pending setup")} className="btn btn-outline btn-sm" data-testid="access-save-setup-btn">{a.status === "pending" || prep ? "Save changes" : "Save as pending setup"}</button>
        {onDone && <button onClick={onDone} className="btn btn-quiet btn-sm" data-testid="access-edit-cancel">Cancel</button>}
      </div>
      {canSend && a.can_grant && (
        <div className="mt-4 pt-4 border-t border-line" data-testid="access-grant">
          <p className="t-meta">{a.email} already has a verified KNOWN account. You can add them to your church without an invitation.</p>
          {!confirm ? <button disabled={busy} onClick={() => setConfirm(true)} className="btn btn-outline btn-sm mt-2" data-testid="access-grant-btn">Add directly</button> : (
            <div className="flex flex-wrap gap-2 mt-2">
              <button disabled={busy} onClick={() => run("grant", "Access granted")} className="btn btn-primary btn-sm" data-testid="access-grant-confirm">Confirm access</button>
              <button onClick={() => setConfirm(false)} className="btn btn-quiet btn-sm" data-testid="access-grant-cancel">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Prepared = ({ a, reload, onEdit }) => {
  const { api, membership } = useOrg();
  const prep = a.prepared;
  const names = (prep.groups || []).map((g) => g.name);
  const act = (method, path, body, msg) => api(path, { method, body }).then((r) => { toast.success(r?.email_sent === false ? "Invitation created, but the email didn't send." : msg); reload(); }).catch((e) => toast.error(e.message));
  return (
    <div data-testid="access-prepared" className={a.status === "pending" ? "mt-4 pt-4 border-t border-line" : ""}>
      {a.status === "none" && <p className="status status-attention">Pending setup</p>}
      <p className="t-meta mt-2" data-testid="access-prepared-summary">
        Planned as {roleLabel(membership, prep.role)}{names.length ? ` · leads ${names.join(", ")}` : ""}. Nothing is active until they accept an invitation.
      </p>
      <div className="flex flex-wrap gap-2 mt-3">
        {a.status === "none" && !a.issue && <button onClick={() => act("POST", `/people/${a.person.id}/invite`, { role: prep.role }, "Invitation sent")} className="btn btn-primary btn-sm" data-testid="access-send-prepared-btn">Send invitation</button>}
        <button onClick={onEdit} className="btn btn-outline btn-sm" data-testid="access-edit-setup-btn">Edit setup</button>
        {a.status === "none" && <button onClick={() => act("DELETE", `/people/${a.person.id}/prepare`, undefined, "Setup removed")} className="btn btn-quiet btn-sm" data-testid="access-remove-setup-btn">Remove setup</button>}
      </div>
    </div>
  );
};

const Pending = ({ a, reload }) => {
  const { api, membership } = useOrg();
  const act = (method, path, msg) => api(path, { method }).then(() => { toast.success(msg); reload(); }).catch((e) => toast.error(e.message));
  const inv = a.invitation;
  return (
    <div data-testid="access-pending">
      <p className="status status-attention">Pending invitation</p>
      <p className="t-meta mt-2 [overflow-wrap:anywhere]">{roleLabel(membership, inv.role)} · sent to {inv.email}</p>
      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={() => act("POST", `/invitations/${inv.id}/resend`, "Invitation resent")} className="btn btn-outline btn-sm" data-testid="access-resend-btn">Resend</button>
        <button onClick={() => act("DELETE", `/invitations/${inv.id}`, "Invitation cancelled")} className="btn btn-quiet btn-sm" data-testid="access-cancel-btn">Cancel</button>
      </div>
    </div>
  );
};

const NotYet = ({ a, reload }) => {
  const [edit, setEdit] = useState(false);
  const blocked = a.issue === "shared_email" || a.issue === "email_is_member";
  return (
    <>
      {a.issue ? <p className="t-meta" data-testid="access-issue">{ISSUE[a.issue]}</p>
        : !a.prepared && <p className="t-meta">{a.person.first_name} doesn't use KNOWN yet. Invitation goes to <b className="text-ink [overflow-wrap:anywhere]">{a.email}</b>.</p>}
      {a.status === "pending" && <Pending a={a} reload={reload} />}
      {!blocked && (a.prepared && !edit ? <Prepared a={a} reload={reload} onEdit={() => setEdit(true)} />
        : (a.status === "none" || edit) && <Plan a={a} reload={reload} onDone={edit ? () => setEdit(false) : undefined} />)}
      {a.status === "pending" && !a.prepared && !edit && <button onClick={() => setEdit(true)} className="btn btn-quiet btn-sm mt-2" data-testid="access-plan-groups-btn">Plan their Care Groups</button>}
    </>
  );
};

export const PersonAccess = ({ p, Section }) => {
  const { api, membership } = useOrg();
  const [a, setA] = useState(null);
  const load = useCallback(() => api(`/people/${p.id}/access`).then(setA).catch(() => setA(null)), [api, p.id]);
  useEffect(() => { load(); }, [load]);
  if (!a) return null;
  return (
    <Section title="KNOWN Access" testid="person-access">
      {a.status === "member" ? (
        <div data-testid="access-member">
          <p className="t-body">{roleLabel(membership, a.membership.role)}{a.membership.status === "disabled" ? " · deactivated" : ""}</p>
          <Link to="/team" className="text-brand font-medium underline underline-offset-4 text-sm mt-1 inline-block" data-testid="access-manage-link">Manage access</Link>
        </div>
      ) : <NotYet a={a} reload={load} />}
    </Section>
  );
};
